import type { MarketingIndustrySlug } from './industry-page-model';
import type { MarketingIndustryPageSource } from './industry-page-content.types';

type DetailIndustrySlug = Extract<
  MarketingIndustrySlug,
  'bags-accessories' | 'eyewear' | 'jewelry-watches'
>;

export const MARKETING_INDUSTRY_PAGE_CONTENT_D = {
  'bags-accessories': {
    heroBody:
      'Turn sketches and references into realistic concepts, component variations, development specifications, BOMs, and launch content.',
    categories: [
      'Handbags',
      'Backpacks',
      'Travel',
      'Small Leather Goods',
      'Belts',
    ],
    benefitsHeading: 'Resolve more before the first sample',
    benefits: [
      {
        title: 'Fewer sample rounds',
        body: 'Catch proportion, material, and construction issues earlier.',
      },
      {
        title: 'Protect target margin',
        body: 'Compare material, hardware, and build options before committing.',
      },
      {
        title: 'Faster factory handoff',
        body: 'Keep approved visuals, specifications, and BOMs connected.',
      },
    ],
    workflowHeading: 'One bag, connected from sketch to sale',
    workflowSteps: [
      {
        number: '01',
        title: 'Set the Direction',
        body: 'Define the customer, use case, target price, silhouette, and collection role.',
        output: 'Product brief',
      },
      {
        number: '02',
        title: 'Develop the Form',
        body: 'Explore proportion, structure, capacity, openings, compartments, and carry options.',
        output: 'Approved concept',
      },
      {
        number: '03',
        title: 'Resolve Components',
        body: 'Apply materials, linings, edge paint, thread, hardware, closures, branding, and colorways.',
        output: 'Component and material map',
      },
      {
        number: '04',
        title: 'Prepare Development',
        body: 'Create technical views, measurements, callouts, BOMs, packaging, and cost comparisons.',
        output: 'Development pack',
      },
      {
        number: '05',
        title: 'Review and Launch',
        body: 'Record sample comments, approve revisions, and carry the final product into commerce and campaigns.',
        output: 'Approved product record',
      },
    ],
    capabilitiesHeading: 'See changes before you commit',
    capabilities: [
      {
        title: 'Form and function',
        body: 'Silhouette, proportion, dimensions, capacity, compartments, handles, straps, and openings.',
      },
      {
        title: 'Materials and finish',
        body: 'Leather, textiles, alternatives, lining, grain, edge paint, stitching, and color.',
      },
      {
        title: 'Construction and hardware',
        body: 'Panels, seams, reinforcements, closures, buckles, D-rings, feet, and branding.',
      },
      {
        title: 'Cost and readiness',
        body: 'BOMs, consumption, supplier references, MOQ, cost, samples, and testing records.',
      },
    ],
    systemsHeading: 'Continue using your existing systems',
    systemsBody:
      'IMAI can work alongside CAD, PLM, ERP, PIM, DAM, supplier, and commerce systems while specialist tools continue to handle patterns, hardware engineering, tooling, purchasing, and finance.',
    recordHeading: 'Or make My Drive your complete product record',
    recordBody:
      'Keep briefs, concepts, components, materials, measurements, BOMs, samples, supplier documents, testing records, imagery, video, and channel assets connected.',
    faq: {
      label: 'Frequently Asked Questions',
      heading: 'FAQ',
      items: [
        {
          question: 'Can IMAI support more than handbags?',
          answer:
            'Yes. It can support handbags, backpacks, travel products, small leather goods, belts, and related accessories.',
        },
        {
          question: 'Does IMAI replace CAD or PLM?',
          answer:
            'Not necessarily. It can work alongside existing systems while connecting product decisions and outputs across teams.',
        },
        {
          question: 'How does IMAI reduce sample rounds?',
          answer:
            'Teams can resolve proportion, placement, materials, colorways, and construction details before physical sampling.',
        },
        {
          question: 'Does IMAI replace physical testing?',
          answer:
            'No. Physical samples remain essential for validating structure, usability, hand feel, load performance, and durability.',
        },
      ],
    },
    cta: {
      eyebrow: 'One product record from sketch to sale',
      heading: 'Get the product right before sampling.',
      cards: [
        { label: 'Build a Bag Concept', href: '/auth?mode=signup' },
        { label: 'Book a Bags & Accessories Demo', href: '/contact' },
      ],
    },
  },
  eyewear: {
    heroBody:
      'Create optical and sun concepts, explore shapes, materials, lenses, and colorways, then prepare development and launch assets from one record.',
    categories: ['Optical Frames', 'Sunglasses', 'Readers', 'Fashion Eyewear'],
    benefitsHeading: 'Create more before you commit',
    benefits: [
      {
        title: 'More frame directions',
        body: 'Explore shapes, materials, and colorways faster.',
      },
      {
        title: 'Fewer late changes',
        body: 'Resolve more before prototypes and tooling.',
      },
      {
        title: 'Every variant connected',
        body: 'Keep optical, sun, size, color, and lens options aligned.',
      },
    ],
    workflowHeading: 'One frame, connected from brief to launch',
    workflowSteps: [
      {
        number: '01',
        title: 'Define the Collection',
        body: 'Set the customer, category, target price, brand codes, and collection plan.',
      },
      {
        number: '02',
        title: 'Explore Frame Directions',
        body: 'Compare lens shapes, rims, bridges, temples, materials, and design details.',
      },
      {
        number: '03',
        title: 'Review and Refine',
        body: 'Evaluate proportions on representative faces and develop optical, sun, size, color, and lens variants.',
      },
      {
        number: '04',
        title: 'Prepare Development',
        body: 'Create drawings, dimensions, component details, BOMs, markings, packaging, and cost comparisons.',
      },
      {
        number: '05',
        title: 'Validate and Launch',
        body: 'Record prototype comments, approve revisions, and carry the final frame into sales and campaign content.',
      },
    ],
    capabilitiesHeading: 'See every decision in context',
    capabilities: [
      {
        title: 'Shape and proportion',
        body: 'Lens shape, rim, bridge, temples, frame dimensions, and on-face proportion.',
      },
      {
        title: 'Materials and color',
        body: 'Acetate, metal, injected materials, finishes, lens tints, and detailing.',
      },
      {
        title: 'Construction and fit',
        body: 'Eye size, bridge, width, lens height, temple length, hinges, curvature, and weight.',
      },
      {
        title: 'Cost and readiness',
        body: 'BOMs, supplier references, MOQ, tooling, target cost, prototypes, and test records.',
      },
    ],
    systemsHeading: 'Keep your specialist eyewear systems',
    systemsBody:
      'Work alongside eyewear CAD, PLM, ERP, lens, supplier, PIM, DAM, and commerce systems while specialists retain optical engineering, tooling, fitting, and testing.',
    recordHeading: 'Or connect the complete journey in My Drive',
    recordBody:
      'Keep briefs, dimensions, components, materials, BOMs, variants, suppliers, tests, markings, packaging, imagery, and video connected to the approved frame.',
    faq: {
      label: 'Frequently Asked Questions',
      heading: 'FAQ',
      items: [
        {
          question: 'Can IMAI support both optical frames and sunglasses?',
          answer:
            'Yes. Teams can develop optical, sun, reader, and fashion-eyewear collections.',
        },
        {
          question: 'Can IMAI manage frame variants?',
          answer:
            'Yes. Size, material, color, optical, sun, and lens options can remain connected to the approved frame.',
        },
        {
          question: 'Does on-face visualization confirm physical fit?',
          answer:
            'No. It helps evaluate proportion. Physical prototypes and professional fitting remain necessary for final validation.',
        },
        {
          question: 'Does IMAI certify eyewear compliance?',
          answer:
            'No. Qualified teams and accredited laboratories remain responsible while IMAI keeps requirements, reports, and approvals connected.',
        },
      ],
    },
    cta: {
      eyebrow: 'One frame architecture. The complete collection.',
      heading: 'Build the collection before the first prototype.',
      cards: [
        { label: 'Build an Eyewear Collection', href: '/auth?mode=signup' },
        { label: 'Book an Eyewear Demo', href: '/contact' },
      ],
    },
  },
  'jewelry-watches': {
    heroBody:
      'Turn a natural gemstone, sketch, or watch brief into a high-fidelity collection with every material, component, and launch asset connected.',
    categories: [
      'Jewellery',
      'Watches',
      'Gemstones',
      'Components',
      'Variants',
      'Provenance',
    ],
    benefitsHeading: 'See every detail before it is made',
    benefits: [
      {
        title: 'Let the stone lead the design',
        body: 'Work with a gem’s real outline, orientation, inclusions, color, and character.',
      },
      {
        title: 'Resolve every watch component',
        body: 'Develop the case, dial, hands, crown, crystal, movement, strap, and bracelet together.',
      },
      {
        title: 'Keep every variant aligned',
        body: 'Connect materials, components, certificates, suppliers, approvals, and stories.',
      },
    ],
    workflowHeading: 'One product record from material to launch',
    workflowSteps: [
      {
        number: '01',
        title: 'Capture the Starting Point',
        body: 'Record the stone, sketch, wearer, use case, target price, dimensions, movement, or collection plan.',
      },
      {
        number: '02',
        title: 'Develop the Complete Piece',
        body: 'Explore jewellery forms around the real gem or resolve every external watch component together.',
      },
      {
        number: '03',
        title: 'Resolve Materials and Components',
        body: 'Connect settings, metals, stones, cases, dials, hands, finishes, straps, and supporting structures.',
      },
      {
        number: '04',
        title: 'Prepare and Launch',
        body: 'Organize drawings, dimensions, maps, BOMs, suppliers, costs, tests, and premium launch content.',
      },
    ],
    capabilitiesHeading: 'Decisions for jewellery and watches',
    capabilities: [
      {
        title: 'Material truth',
        body: 'Natural form, dimensions, color, inclusions, certificates, metals, movements, and source references.',
      },
      {
        title: 'Design architecture',
        body: 'Jewellery type, setting, case dimensions, dial layout, hands, indices, crown, and straps.',
      },
      {
        title: 'Make and cost',
        body: 'Component maps, target weights, BOMs, suppliers, tooling, MOQ, and cost inputs.',
      },
      {
        title: 'Product truth',
        body: 'Treatments, disclosures, provenance, chain of custody, hallmarks, tests, and approved claims.',
      },
    ],
    systemsHeading: 'Keep your specialist systems and crafts',
    systemsBody:
      'Work alongside jewellery CAD/CAM, watch engineering, PLM, ERP, supplier, inventory, PIM, DAM, and commerce systems. Specialists remain responsible for production geometry, setting, casting, machining, hallmarking, and testing.',
    recordHeading: 'Or connect idea, development, production, and launch',
    recordBody:
      'Use My Drive to keep designs, components, materials, variants, certificates, approvals, supplier records, macro imagery, provenance stories, and launch assets together.',
    faq: {
      label: 'Frequently Asked Questions',
      heading: 'FAQ',
      items: [
        {
          question: 'Can I start with an individual natural gemstone?',
          answer:
            'Yes. Begin with an image, scan, dimensions, and available gem information, then explore designs around its actual form.',
        },
        {
          question: 'Does IMAI replace jewellery CAD or craftspeople?',
          answer:
            'No. Specialist systems and craftspeople remain responsible for production geometry, setting, and technical validation.',
        },
        {
          question:
            'Can IMAI support different watch movements and components?',
          answer:
            'Yes. Movement, case, dial, strap, and component references can remain connected to each watch variant.',
        },
        {
          question: 'Does IMAI certify jewellery or watch performance?',
          answer:
            'No. Qualified craftspeople, engineers, assay offices, and laboratories remain responsible for certification and testing.',
        },
      ],
    },
    cta: {
      eyebrow: 'Every detail, variant, and story connected',
      heading: 'See the complete piece before it is made.',
      cards: [
        { label: 'Design Around a Gem', href: '/auth?mode=signup' },
        { label: 'Discuss Your Jewelry or Watch Collection', href: '/contact' },
      ],
    },
  },
} as const satisfies Record<DetailIndustrySlug, MarketingIndustryPageSource>;
